import React from "react";
import { useQuery } from "@tanstack/react-query";
import { httpsCallable } from "firebase/functions";
import { useAuth } from "@core/auth/AuthProvider";
import { getFirebaseServices } from "@core/config/firebase";

const OrganizationContext = React.createContext(null);
const DEMO_BUSINESS_STORAGE_KEY = "venpro_demo_business.nwas.v1";

const DEMO_MEMBERS_STORAGE_KEY = "venpro_demo_members.v1";

const defaultDemoMembers = [
  { id: "demo-member-1", userId: "demo-vendor-owner", displayName: "Alex Vance", email: "alex@nwautosupreme.com", role: "owner", status: "active", isOwner: true },
  { id: "demo-member-2", userId: "demo-tech-1", displayName: "Dave Miller", email: "dave@nwautosupreme.com", role: "staff", status: "active", isOwner: false },
  { id: "demo-member-3", userId: "demo-office-1", displayName: "Sarah Jenkins", email: "sarah@nwautosupreme.com", role: "office", status: "active", isOwner: false },
];

const demoOrganization = {
  id: "vendor_org_demo_nwas",
  name: "NW Auto Supreme",
  shortName: "NWAS",
  slug: "nwautosupreme",
  phone: "(206) 555-0199",
  email: "service@nwautosupreme.com",
  website: "https://nwautosupreme.com",
  address: "14820 Highway 99",
  city: "Lynnwood",
  state: "WA",
  zip: "98087",
  tagline: "Precision Dealership & Retail Recon Specialists",
  description: "Mobile and bay automotive restyling, ceramic window tint, paint protection film, and interior reconditioning.",
  role: "owner",
  permissions: [
    "jobs.view",
    "jobs.status.update",
    "jobs.assign",
    "jobs.cancel",
    "jobs.photos.manage",
    "calendar.view",
    "calendar.schedule",
    "invoices.view",
    "invoices.manage",
    "invoices.payment.record",
    "catalogue.view",
    "catalogue.manage",
    "dealerships.view",
    "dealerships.manage",
    "notifications.view",
    "users.view",
    "users.manage",
    "users.permissions.manage",
    "organizations.manage",
  ],
};

function getDemoOrganizations(ownerUid) {
  try {
    const business = JSON.parse(localStorage.getItem(DEMO_BUSINESS_STORAGE_KEY) || "null");
    if (business && typeof business === "object") {
      return [{ ...demoOrganization, ...business, ownerUid }];
    }
    return [{ ...demoOrganization, ownerUid }];
  } catch {
    return [{ ...demoOrganization, ownerUid }];
  }
}

function normalizeOrganization(value) {
  if (!value || typeof value !== "object") return null;
  const id = String(value.id ?? value.organizationId ?? "").trim();
  const name = String(value.name ?? value.organizationName ?? "").trim();
  const shortName = String(value.shortName ?? "").trim();
  const slug = String(value.slug ?? id.replace(/^vendor_/, "")).trim();
  if (!id || !name) return null;

  return {
    id,
    name,
    slug,
    ...(shortName ? { shortName } : {}),
    phone: value.phone ?? null,
    email: value.email ?? null,
    website: value.website ?? null,
    address: value.address ?? null,
    city: value.city ?? null,
    state: value.state ?? null,
    zip: value.zip ?? null,
    tagline: value.tagline ?? null,
    description: value.description ?? null,
    role: String(value.role ?? "member"),
    permissions: Array.isArray(value.permissions)
      ? value.permissions.filter((permission) => typeof permission === "string")
      : [],
  };
}

async function listOrganizations() {
  const services = getFirebaseServices();
  if (!services?.functions) throw new Error("Firebase Functions are not configured.");

  const callable = httpsCallable(services.functions, "vendorsListOrganizations");
  const result = await callable({});
  const values = Array.isArray(result.data?.organizations) ? result.data.organizations : [];
  return {
    organizations: values.map(normalizeOrganization).filter(Boolean),
    platformRole: result.data?.platformRole === "superadmin" ? "superadmin" : null,
  };
}

export function OrganizationProvider({ children }) {
  const { user, isDemoMode, organizationRevision } = useAuth();
  const [selectedOrganizationId, setSelectedOrganizationId] = React.useState(null);
  const [demoState, setDemoState] = React.useState(() => getDemoOrganizations(user?.uid));
  const query = useQuery({
    queryKey: ["vendor-organizations", user?.uid, organizationRevision],
    queryFn: listOrganizations,
    enabled: Boolean(user) && !isDemoMode,
    staleTime: 60_000,
  });

  const organizations = React.useMemo(
    () => (isDemoMode ? demoState : (query.data?.organizations ?? [])),
    [isDemoMode, demoState, query.data],
  );
  const platformRole = isDemoMode ? null : (query.data?.platformRole ?? null);
  const isSuperadmin = platformRole === "superadmin";
  const activeOrganization = organizations.find(
    (organization) => organization.id === selectedOrganizationId,
  ) ?? organizations[0] ?? null;
  const permissions = React.useMemo(
    () => new Set(activeOrganization?.permissions ?? []),
    [activeOrganization],
  );

  React.useEffect(() => {
    if (!activeOrganization) {
      setSelectedOrganizationId(null);
      return;
    }
    if (activeOrganization.id !== selectedOrganizationId) {
      setSelectedOrganizationId(activeOrganization.id);
    }
  }, [activeOrganization, selectedOrganizationId]);

  const selectOrganization = React.useCallback((organizationId) => {
    if (organizations.some((organization) => organization.id === organizationId)) {
      setSelectedOrganizationId(organizationId);
    }
  }, [organizations]);

  const can = React.useCallback(
    (permission) => !permission || isSuperadmin || permissions.has(permission),
    [isSuperadmin, permissions],
  );

  const updateBusinessProfile = React.useCallback(async (updates) => {
    if (isDemoMode) {
      const current = getDemoOrganizations(user?.uid)[0];
      const merged = { ...current, ...updates };
      localStorage.setItem(DEMO_BUSINESS_STORAGE_KEY, JSON.stringify(merged));
      setDemoState([merged]);
      return merged;
    }
    const services = getFirebaseServices();
    if (!services?.functions) throw new Error("Firebase Functions are not configured.");
    const callable = httpsCallable(services.functions, "vendorsUpdateBusinessProfile");
    const result = await callable({ organizationId: activeOrganization?.id, ...updates });
    await query.refetch();
    return result.data?.organization;
  }, [activeOrganization?.id, isDemoMode, query, user?.uid]);

  const listMembers = React.useCallback(async () => {
    if (isDemoMode) {
      try {
        const stored = JSON.parse(localStorage.getItem(DEMO_MEMBERS_STORAGE_KEY) || "null");
        if (Array.isArray(stored)) return stored;
      } catch {
        // use default
      }
      return defaultDemoMembers;
    }
    const services = getFirebaseServices();
    if (!services?.functions) throw new Error("Firebase Functions are not configured.");
    const callable = httpsCallable(services.functions, "authListOrganizationMembers");
    const result = await callable({ organizationId: activeOrganization?.id });
    return Array.isArray(result.data?.members) ? result.data.members : [];
  }, [activeOrganization?.id, isDemoMode]);

  const addMember = React.useCallback(async (memberData) => {
    if (isDemoMode) {
      const current = await listMembers();
      const newMember = {
        id: `demo-member-${Date.now()}`,
        userId: `demo-user-${Date.now()}`,
        displayName: memberData.displayName || memberData.email.split("@")[0],
        email: memberData.email,
        role: memberData.role || "staff",
        status: "active",
        isOwner: false,
      };
      const next = [...current, newMember];
      localStorage.setItem(DEMO_MEMBERS_STORAGE_KEY, JSON.stringify(next));
      return newMember;
    }
    const services = getFirebaseServices();
    if (!services?.functions) throw new Error("Firebase Functions are not configured.");
    const callable = httpsCallable(services.functions, "authAddOrganizationMember");
    const result = await callable({ organizationId: activeOrganization?.id, ...memberData });
    return result.data?.member;
  }, [activeOrganization?.id, isDemoMode, listMembers]);

  const value = React.useMemo(() => ({
    organizations,
    activeOrganization,
    platformRole,
    isSuperadmin,
    isLoading: !isDemoMode && query.isLoading,
    error: query.error ?? null,
    selectOrganization,
    can,
    updateBusinessProfile,
    listMembers,
    addMember,
    refreshOrganizations: query.refetch,
  }), [
    activeOrganization,
    addMember,
    can,
    isDemoMode,
    isSuperadmin,
    listMembers,
    organizations,
    platformRole,
    query.error,
    query.isLoading,
    query.refetch,
    selectOrganization,
    updateBusinessProfile,
  ]);

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
}

export function useOrganization() {
  const context = React.useContext(OrganizationContext);
  if (!context) {
    throw new Error("useOrganization must be used inside OrganizationProvider.");
  }
  return context;
}
